import type { RowActionType, UUID } from "@/lib/types";

/**
 * The default notification recipients for a workflow action, as a pure decision so
 * it can be tested. The component resolves the id lists (members, creator, …) and
 * hands them in; this only maps action → which of those lists to pre-fill.
 */
export interface ActionRecipientContext {
  /** The ticket creator (row.created_by). */
  createdBy: UUID | null;
  /** Who last acted on the row (row.last_actor_user_id). */
  lastActorUserId: UUID | null;
  /** Department heads = workspace owners. */
  ownerIds: UUID[];
  /** Every board member except the viewer. */
  otherMemberIds: UUID[];
  /** The viewer — never default to notifying yourself. */
  meId: UUID | null;
}

/** Leicht transitions notify no one (the dialog hides the recipient picker). */
const SILENT: readonly RowActionType[] = ["sichten", "einplanen", "zurueckstellen"];

/**
 * Actions that default to notifying the TICKET CREATOR — the requester stays in the
 * loop. 🔴 `senden_zur_pruefung` lives here (colleague request 2026-09-22): the IT
 * staff actions Annehmen / Senden zur Prüfung / Lösen should all pre-select the
 * person who created the ticket, not another IT colleague. (It previously defaulted
 * to all other members.) The recipient stays editable in the dialog.
 */
const NOTIFY_CREATOR: readonly RowActionType[] = [
  "annehmen",
  "senden_zur_pruefung",
  "zuweisen",
  "wartet_auf_kunde",
  "loesen",
  "in_review",
  "annehmen_idee",
  "ablehnen_idee",
];

export function defaultActionRecipients(
  action: RowActionType,
  ctx: ActionRecipientContext,
): UUID[] {
  if (SILENT.includes(action)) return [];
  let ids: UUID[];
  if (action === "eskalieren") {
    // Escalate → the department head(s); the backend also merges owners in server-side.
    ids = ctx.ownerIds;
  } else if (NOTIFY_CREATOR.includes(action)) {
    ids = ctx.createdBy ? [ctx.createdBy] : [];
  } else if (ctx.lastActorUserId) {
    // Bestätigen / Erneut öffnen (creator-side) → the staff member who last acted.
    ids = [ctx.lastActorUserId];
  } else {
    ids = ctx.otherMemberIds;
  }
  return ctx.meId ? ids.filter((id) => id !== ctx.meId) : ids;
}
